import type { CSSProperties } from 'react';
import type { OddsView } from './types';
import { tierColorFor } from './tierStyles';
import { TierDot } from '../design-system/TierDot';
import { ExpectedValueBadge } from '../design-system/ExpectedValueBadge';

export interface OddsPanelProps {
  data: OddsView;
  disclaimerHref?: string;
  chanceLabel?: string;
}

/**
 * Classic OddsPanel — wired to the shared `StatsRow` Design Constructor
 * surface so a single registry entry themes both Classic + Modern. All
 * `var(--co-OddsCard-*, …)` fallbacks mirror the previous hardcoded
 * Classic styling so unmodified deployments stay pixel-identical.
 *
 * Knob coverage:
 *   • `fontFamily` — root font stack.
 *   • `bgFrom` / `bgTo` / `bgDirection` / `bgOpacity` — primary glass wash.
 *     The secondary `rgba(0,0,0,0.18)` underlay stays fixed because Classic
 *     stacks two gradient layers and StatsRow only exposes one pair.
 *   • `glassBlur` — backdrop-filter blur radius (with `-webkit-` prefix).
 *   • `borderColor` / `borderWidth` — outer border. Classic default = 2px
 *     even though the registry default is 0 — the fallback keeps Classic at
 *     `border-2` until an operator overrides it.
 *   • `labelColor` / `labelSize` — caption text ("ODDS", "Big win chance",
 *     "Expected Value", tier names, "% Chance" suffix).
 *   • `valueColor` / `valueSize` — numeric text ("33.34%" headline,
 *     "$5 – $15" tier ranges).
 *
 * Per-node `labelSize` / `valueSize` fallbacks intentionally differ between
 * headline-weight (18px) and subhead-weight (13px) nodes so that an
 * UNMODIFIED panel matches the previous `type-headline` / `type-subhead`
 * pixel sizes. When the operator overrides the knob, every node converges
 * on the same size — that is the documented behaviour of a one-knob row.
 *
 * `<ExpectedValueBadge>` and `<TierDot>` are NOT re-themed here: they have
 * their own Design Constructor entries with their own knobs.
 */
export function OddsPanel({
  data,
  disclaimerHref = '#',
  chanceLabel = 'Big win chance',
}: OddsPanelProps) {
  // Background: bgFrom→bgTo gradient driven by knobs, with the classic
  // secondary black underlay (18% black) preserved as a fixed second layer.
  const sectionStyle: CSSProperties = {
    fontFamily: 'var(--co-OddsCard-fontFamily), Inter, system-ui, sans-serif',
    backgroundImage:
      'linear-gradient(var(--co-OddsCard-bgDirection, to bottom), ' +
      'color-mix(in srgb, var(--co-OddsCard-bgFrom, rgba(255,255,255,0.2)) calc(var(--co-OddsCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
      'color-mix(in srgb, var(--co-OddsCard-bgTo, rgba(153,153,153,0.2)) calc(var(--co-OddsCard-bgOpacity, 1) * 100%), transparent) 100%), ' +
      'linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.18) 100%)',
    backdropFilter: 'blur(var(--co-OddsCard-glassBlur, 27px))',
    WebkitBackdropFilter: 'blur(var(--co-OddsCard-glassBlur, 27px))',
    borderStyle: 'solid',
    borderWidth: 'var(--co-OddsCard-borderWidth, 2px)',
    borderColor: 'var(--co-OddsCard-borderColor, rgba(171,174,179,0.24))',
  };

  // "ODDS" heading — uses `titleColor`/`titleSize`, separate from labels
  // so the operator can make the section title bigger/different colour
  // without dragging every "Big win chance" / "Common" label along.
  const headlineLabelStyle: CSSProperties = {
    color: 'var(--co-OddsCard-titleColor, rgba(255,255,255,1))',
    fontSize: 'var(--co-OddsCard-titleSize, 18px)',
  };
  // Big-win-chance value ("33.34%") still uses valueColor/valueSize but
  // with an 18px fallback so unmodified type-headline pixels stay intact.
  const headlineValueStyle: CSSProperties = {
    color: 'var(--co-OddsCard-valueColor, #ffffff)',
    fontSize: 'var(--co-OddsCard-valueSize, 18px)',
  };

  // Subhead-weight nodes — fallback to 13px.
  const subheadLabelStyle: CSSProperties = {
    color: 'var(--co-OddsCard-labelColor, rgba(255,255,255,0.5))',
    fontSize: 'var(--co-OddsCard-labelSize, 13px)',
  };
  const subheadValueStyle: CSSProperties = {
    color: 'var(--co-OddsCard-valueColor, #ffffff)',
    fontSize: 'var(--co-OddsCard-valueSize, 13px)',
  };

  return (
    <section
      data-co-component="OddsCard"
      className="
        relative overflow-hidden [border-radius:var(--co-OddsCard-borderRadius,28px)]
        pt-[10px] px-[13px] pb-[17px]
        shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
        text-white
      "
      style={sectionStyle}
      aria-label="Pack odds"
    >
      <header className="mb-4 grid grid-cols-3 items-start">
        <div className="flex flex-col">
          <p className="type-headline font-bold uppercase" style={headlineLabelStyle}>
            Odds
          </p>
          <a
            href={disclaimerHref}
            className="type-subhead underline opacity-50 hover:opacity-80 w-fit"
          >
            Disclaimer
          </a>
        </div>

        <div className="flex flex-col items-end">
          <span className="type-subhead opacity-50" style={subheadLabelStyle}>
            {chanceLabel}
          </span>
          <span className="type-headline font-semibold" style={headlineValueStyle}>
            {formatPercent(data.bigWinChance)}%
          </span>
        </div>

        <div className="flex flex-col items-end">
          <span className="type-subhead opacity-50" style={subheadLabelStyle}>
            Expected Value
          </span>
          <ExpectedValueBadge value={`$${data.estimatedValue.toFixed(2)}`} />
        </div>
      </header>

      <ul className="flex flex-col gap-[6px]">
        {data.tiers.map((tier) => (
          <li
            key={tier.tierName + tier.range}
            className="grid grid-cols-3 items-center"
          >
            <span className="flex items-center gap-2">
              <TierDot color={tierColorFor(tier.tierName)} />
              <span className="type-subhead" style={subheadLabelStyle}>
                {tier.tierName}
              </span>
            </span>
            <span className="type-subhead text-right" style={subheadValueStyle}>
              {tier.range}
            </span>
            <span
              className="type-subhead text-right whitespace-nowrap"
              style={subheadLabelStyle}
            >
              {formatPercent(tier.percentage)}% Chance
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}
